import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import StatusChip from '@/components/common/StatusChip';
import { useDateFormat } from '@/hooks/useDateFormat';
import type {
  GateEntryTurnaroundRow,
  PurchaseOrderRegisterRow,
  PurchaseRequestRegisterRow,
  SupplierPerformanceRow,
} from '@/types/reports';
import { withId } from './reportHelpers';

const TABS = ['purchaseRequestRegister', 'purchaseOrderRegister', 'supplierPerformance', 'gateEntryTurnaround'] as const;

export default function ProcurementReportsPage() {
  const { t } = useTranslation('reports');
  const { formatDate } = useDateFormat();
  const [tab, setTab] = useState(0);

  const { data: purchaseRequests = [], isLoading: loadingPr } = useQuery({
    queryKey: ['reports', 'purchase-request-register'],
    queryFn: async () => (await apiClient.get<PurchaseRequestRegisterRow[]>('/reports/purchase-request-register')).data,
    enabled: tab === 0,
  });
  const { data: purchaseOrders = [], isLoading: loadingPo } = useQuery({
    queryKey: ['reports', 'purchase-order-register'],
    queryFn: async () => (await apiClient.get<PurchaseOrderRegisterRow[]>('/reports/purchase-order-register')).data,
    enabled: tab === 1,
  });
  const { data: supplierPerformance = [], isLoading: loadingSupplier } = useQuery({
    queryKey: ['reports', 'supplier-performance'],
    queryFn: async () => (await apiClient.get<SupplierPerformanceRow[]>('/reports/supplier-performance')).data,
    enabled: tab === 2,
  });
  const { data: gateEntries = [], isLoading: loadingGate } = useQuery({
    queryKey: ['reports', 'gate-entry-turnaround'],
    queryFn: async () => (await apiClient.get<GateEntryTurnaroundRow[]>('/reports/gate-entry-turnaround')).data,
    enabled: tab === 3,
  });

  const purchaseRequestColumns: DataTableColumn<ReturnType<typeof withId<PurchaseRequestRegisterRow>>[number]>[] = [
    { field: 'prNumber', headerName: t('columns.number') },
    { field: 'requestedByUserName', headerName: t('columns.requestedBy'), filterable: true },
    { field: 'warehouseName', headerName: t('columns.warehouse'), filterable: true },
    { field: 'requestDate', headerName: t('columns.date'), render: (r) => formatDate(r.requestDate) },
    { field: 'status', headerName: t('columns.status'), render: (r) => (r.isClosed ? <StatusChip status="Closed" /> : <StatusChip status={r.status} />), filterable: true },
    { field: 'materialCode', headerName: t('columns.materialCode') },
    { field: 'materialName', headerName: t('columns.materialName') },
    { field: 'requestedQty', headerName: t('columns.requestedQty') },
  ];

  const purchaseOrderColumns: DataTableColumn<ReturnType<typeof withId<PurchaseOrderRegisterRow>>[number]>[] = [
    { field: 'poNumber', headerName: t('columns.number') },
    { field: 'supplierName', headerName: t('columns.supplier'), filterable: true },
    { field: 'warehouseName', headerName: t('columns.warehouse'), filterable: true },
    { field: 'orderDate', headerName: t('columns.orderDate'), render: (r) => formatDate(r.orderDate) },
    { field: 'expectedDate', headerName: t('columns.expectedDate'), render: (r) => (r.expectedDate ? formatDate(r.expectedDate) : '—') },
    { field: 'status', headerName: t('columns.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    { field: 'materialCode', headerName: t('columns.materialCode') },
    { field: 'materialName', headerName: t('columns.materialName') },
    { field: 'orderedQty', headerName: t('columns.orderedQty') },
    { field: 'unitPrice', headerName: t('columns.unitPrice'), render: (r) => `₹${r.unitPrice.toFixed(2)}` },
    { field: 'receivedQty', headerName: t('columns.receivedQty') },
    { field: 'pendingQty', headerName: t('columns.pendingQty') },
  ];

  const supplierPerformanceColumns: DataTableColumn<ReturnType<typeof withId<SupplierPerformanceRow>>[number]>[] = [
    { field: 'supplierName', headerName: t('columns.supplier'), filterable: true },
    { field: 'totalPurchaseOrders', headerName: t('columns.totalOrders') },
    { field: 'totalOrderedQty', headerName: t('columns.orderedQty') },
    { field: 'totalReceivedQty', headerName: t('columns.receivedQty') },
    { field: 'totalRejectedQty', headerName: t('columns.rejectedQty') },
    { field: 'rejectionRatePercent', headerName: t('columns.rejectionRate'), render: (r) => (r.rejectionRatePercent != null ? `${r.rejectionRatePercent}%` : '—') },
    { field: 'onTimeDeliveryPercent', headerName: t('columns.onTimeDelivery'), render: (r) => (r.onTimeDeliveryPercent != null ? `${r.onTimeDeliveryPercent}%` : '—') },
  ];

  const gateEntryColumns: DataTableColumn<ReturnType<typeof withId<GateEntryTurnaroundRow>>[number]>[] = [
    { field: 'gateEntryNo', headerName: t('columns.number') },
    { field: 'vehicleNo', headerName: t('columns.vehicleNo'), filterable: true },
    { field: 'poNumber', headerName: t('columns.poNumber'), render: (r) => r.poNumber ?? '—' },
    { field: 'entryDateTime', headerName: t('columns.entryTime'), render: (r) => new Date(r.entryDateTime).toLocaleString() },
    { field: 'exitDateTime', headerName: t('columns.exitTime'), render: (r) => (r.exitDateTime ? new Date(r.exitDateTime).toLocaleString() : '—') },
    { field: 'status', headerName: t('columns.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    { field: 'turnaroundMinutes', headerName: t('columns.turnaroundMinutes'), render: (r) => (r.turnaroundMinutes != null ? Math.round(r.turnaroundMinutes) : '—') },
  ];

  return (
    <>
      <PageHeader title={t('modules.procurement.title')} subtitle={t('modules.procurement.subtitle')} />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable" scrollButtons="auto">
        {TABS.map((key) => (
          <Tab key={key} label={t(`${key}.title`)} />
        ))}
      </Tabs>

      <Box hidden={tab !== 0}>
        <DataTable columns={purchaseRequestColumns} rows={withId(purchaseRequests, (r) => `${r.prId}-${r.materialCode}`)} loading={loadingPr} searchable exportFileName="purchase-request-register" />
      </Box>
      <Box hidden={tab !== 1}>
        <DataTable columns={purchaseOrderColumns} rows={withId(purchaseOrders, (r) => `${r.poId}-${r.materialCode}`)} loading={loadingPo} searchable exportFileName="purchase-order-register" />
      </Box>
      <Box hidden={tab !== 2}>
        <DataTable columns={supplierPerformanceColumns} rows={withId(supplierPerformance, (r) => r.supplierId)} loading={loadingSupplier} searchable exportFileName="supplier-performance" />
      </Box>
      <Box hidden={tab !== 3}>
        <DataTable columns={gateEntryColumns} rows={withId(gateEntries, (r) => r.gateEntryId)} loading={loadingGate} searchable exportFileName="gate-entry-turnaround" />
      </Box>
    </>
  );
}
